import { useState, useEffect, useCallback, useRef } from "react";
import { Reveal } from "@/components/common/Reveal";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { WORLD_MAP_DOTS, LONDON, SINGAPORE, SYDNEY } from "./worldMapDots";

const NAV_LINKS = [
  { label: "Contact form", hash: "#contact", dotColor: "bg-emerald-500" },
  { label: "FAQ", hash: "#faq", dotColor: "bg-sky-500" },
];

const CYCLE_MS = 5000;

const CITIES = [
  { pos: LONDON, label: "London, UK" },
  { pos: SINGAPORE, label: "Singapore" },
  { pos: SYDNEY, label: "Sydney, AU" },
];

const CITY_TRANSLATE = CITIES.map(({ pos: [cx, cy] }) => {
  const dx = ((500 - cx) / 1000) * 100;
  const dy = ((250 - cy) / 500) * 100;
  return `translate(calc(-50% + ${dx.toFixed(1)}%), calc(-50% + ${dy.toFixed(1)}%))`;
});

export function ContactHero() {
  const prefersReducedMotion = useReducedMotion();
  const [activeCity, setActiveCity] = useState(0);
  const [paused, setPaused] = useState(false);
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const resumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-cycle (pauses on hover)
  useEffect(() => {
    if (prefersReducedMotion || paused) return;
    const id = setInterval(() => {
      setActiveCity((prev) => (prev + 1) % CITIES.length);
    }, CYCLE_MS);
    return () => clearInterval(id);
  }, [prefersReducedMotion, paused]);

  const handleMapEnter = useCallback(() => {
    setPaused(true);
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
  }, []);

  const handleMapLeave = useCallback(() => {
    setMousePos(null);
    setPaused(false);
  }, []);

  const handleMapMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    // Map pixel position to SVG viewBox coordinates
    const x = (e.clientX - rect.left) / rect.width * 1000;
    const y = (e.clientY - rect.top) / rect.height * 500;
    setMousePos({ x, y });
  }, []);

  const handleCityClick = useCallback((ci: number) => {
    setActiveCity(ci);
  }, []);

  useEffect(() => {
    return () => { if (resumeTimer.current) clearTimeout(resumeTimer.current); };
  }, []);

  return (
    <section className="relative z-10 flex flex-col items-center pt-[var(--topnav-height)] pb-0 overflow-hidden">
      {/* Title + subtitle + nav */}
      <div className="max-w-6xl mx-auto w-full text-center pt-16 mb-10 px-8">
        <Reveal>
          <h1 className="text-3xl md:text-5xl font-light tracking-tight mb-4 font-[family-name:var(--font-display)]">
            Get in touch
          </h1>
        </Reveal>
        <Reveal delay={100}>
          <p className="text-lg text-[var(--color-fg-secondary)] max-w-2xl mx-auto font-light mb-10">
            Whether you're a firm exploring AI, a potential partner, or just curious about what we're building.
          </p>
        </Reveal>
        <Reveal delay={200}>
          <nav className="flex flex-wrap justify-center gap-4">
            {NAV_LINKS.map((link) => (
              <a
                key={link.label}
                href={link.hash}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] border-b border-[var(--color-border)] hover:border-[var(--color-fg-secondary)] transition-colors"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${link.dotColor}`} />
                <span>{link.label}</span>
              </a>
            ))}
          </nav>
        </Reveal>
      </div>

      {/* Full-width map strip */}
      <Reveal delay={300}>
        <div className="w-screen relative overflow-hidden" style={{ marginTop: "-12px" }}>
          <div className="relative h-[180px] md:h-[240px] overflow-hidden hero-strip-mask">
            <svg
              ref={svgRef}
              viewBox="0 0 1000 500"
              className="absolute h-auto max-w-none"
              preserveAspectRatio="xMidYMid meet"
              style={{
                width: "1400px",
                left: "50%",
                top: "50%",
                transform: CITY_TRANSLATE[activeCity],
                transition: prefersReducedMotion ? "none" : "transform 2.5s ease-in-out",
              }}
              onMouseEnter={handleMapEnter}
              onMouseLeave={handleMapLeave}
              onMouseMove={handleMapMouseMove}
            >
              {/* Filled dot grid world map — dots glow near mouse */}
              {WORLD_MAP_DOTS.map(([x, y], i) => {
                let dotOpacity = 0.1;
                if (mousePos) {
                  const dx = x - mousePos.x;
                  const dy = y - mousePos.y;
                  const dist = Math.sqrt(dx * dx + dy * dy);
                  if (dist < 60) {
                    dotOpacity = 0.1 + (1 - dist / 60) * 0.25;
                  }
                }
                return <circle key={i} cx={x} cy={y} r={1} fill="var(--color-fg)" opacity={dotOpacity} />;
              })}

              {/* City connection lines */}
              <line
                x1={LONDON[0]} y1={LONDON[1]} x2={SINGAPORE[0]} y2={SINGAPORE[1]}
                stroke="var(--color-fg)" strokeWidth="0.5" strokeDasharray="3 3"
                opacity={(activeCity === 0 || activeCity === 1) ? 0.15 : 0.04}
                style={{ transition: "opacity 2s ease" }}
              />
              <line
                x1={SINGAPORE[0]} y1={SINGAPORE[1]} x2={SYDNEY[0]} y2={SYDNEY[1]}
                stroke="var(--color-fg)" strokeWidth="0.5" strokeDasharray="3 3"
                opacity={(activeCity === 1 || activeCity === 2) ? 0.15 : 0.04}
                style={{ transition: "opacity 2s ease" }}
              />
              <line
                x1={LONDON[0]} y1={LONDON[1]} x2={SYDNEY[0]} y2={SYDNEY[1]}
                stroke="var(--color-fg)" strokeWidth="0.5" strokeDasharray="3 3"
                opacity={(activeCity === 2 || activeCity === 0) ? 0.15 : 0.04}
                style={{ transition: "opacity 2s ease" }}
              />

              {/* City highlights — clickable */}
              {CITIES.map((city, ci) => {
                const isActive = activeCity === ci;
                return (
                  <g
                    key={city.label}
                    onClick={() => handleCityClick(ci)}
                    style={{ cursor: "pointer" }}
                  >
                    {/* Larger hit area */}
                    <circle cx={city.pos[0]} cy={city.pos[1]} r={12} fill="transparent" />
                    <circle
                      cx={city.pos[0]} cy={city.pos[1]}
                      r={3} fill="var(--color-fg)" opacity={0.6}
                    />
                    {!prefersReducedMotion && isActive && (
                      <circle
                        key={`p-${ci}-${activeCity}`}
                        cx={city.pos[0]} cy={city.pos[1]} r={3}
                        fill="none" stroke="var(--color-fg)" strokeWidth="0.8"
                        className="animate-[cityPulse_3s_ease-out_infinite]"
                      />
                    )}
                    <text
                      x={city.pos[0] + (ci === 2 ? -8 : 8)}
                      y={city.pos[1] - 8}
                      fill="var(--color-fg)" opacity={isActive ? 0.5 : 0.25}
                      fontSize={8} fontFamily="var(--font-mono)"
                      dominantBaseline="middle"
                      textAnchor={ci === 2 ? "end" : "start"}
                      style={{ transition: "opacity 0.5s ease" }}
                    >
                      {city.label}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        </div>
      </Reveal>

      <style>{`
        @keyframes cityPulse {
          0% { r: 3; opacity: 0.6; }
          100% { r: 20; opacity: 0; }
        }
      `}</style>
    </section>
  );
}
